import type { OpeningHour } from './opening-hour';

export interface OpeningHourStatus {
  readonly isWithinOpeningHours: boolean;
  readonly nextOpeningTime: string | null;
  readonly isClosedToday: boolean;
  /** 1 = Monday … 7 = Sunday; set when closed all day and a later open day exists. */
  readonly nextOpenDayOfWeek: number | null;
  /** Days from today until `nextOpenDayOfWeek` (1 = tomorrow). */
  readonly daysUntilNextOpen: number | null;
}

interface DayTimeSlot {
  readonly fromMinutes: number;
  readonly toMinutes: number;
  readonly fromFormatted: string;
}

interface NextOpenDay {
  readonly dayOfWeek: number;
  readonly daysUntilNextOpen: number;
  readonly openingTime: string;
}

const OPEN_STATUS: OpeningHourStatus = Object.freeze({
  isWithinOpeningHours: true,
  nextOpeningTime: null,
  isClosedToday: false,
  nextOpenDayOfWeek: null,
  daysUntilNextOpen: null,
});

function toTimeInputValue(time: string): string {
  return time.length >= 5 ? time.slice(0, 5) : time;
}

function timeToMinutes(time: string): number {
  const [hours, minutes] = toTimeInputValue(time).split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

/** Converts JS Sunday=0…Saturday=6 to opening-hour Monday=1…Sunday=7. */
export function toOpeningHourDayOfWeek(date: Date): number {
  const javascriptDay = date.getDay();
  return javascriptDay === 0 ? 7 : javascriptDay;
}

/**
 * Restaurant wall-clock day/time for opening-hour checks.
 * Offset `0` (schema default) uses the device local timezone so lunch breaks
 * are not misclassified when UTC differs from local time.
 */
function getRestaurantLocalParts(
  now: Date,
  timezoneOffsetMinutes: number
): { dayOfWeek: number; minutesOfDay: number } {
  if (timezoneOffsetMinutes === 0) {
    return {
      dayOfWeek: toOpeningHourDayOfWeek(now),
      minutesOfDay: now.getHours() * 60 + now.getMinutes(),
    };
  }

  const restaurantAsUtc = new Date(now.getTime() + timezoneOffsetMinutes * 60_000);
  const javascriptDay = restaurantAsUtc.getUTCDay();

  return {
    dayOfWeek: javascriptDay === 0 ? 7 : javascriptDay,
    minutesOfDay: restaurantAsUtc.getUTCHours() * 60 + restaurantAsUtc.getUTCMinutes(),
  };
}

function resolveTimezoneOffsetMinutes(openingHours: readonly OpeningHour[]): number {
  const configuredOffset = openingHours.find(
    (openingHour) => openingHour.timezoneOffsetMinutes !== 0
  )?.timezoneOffsetMinutes;

  return configuredOffset ?? 0;
}

/** Restaurant-local day of week for `date`, using the same timezone rules as status checks. */
export function getRestaurantOpeningHourDayOfWeek(
  openingHours: readonly OpeningHour[],
  date: Date = new Date()
): number {
  return getRestaurantLocalParts(date, resolveTimezoneOffsetMinutes(openingHours)).dayOfWeek;
}

function isWithinSlot(currentMinutes: number, fromMinutes: number, toMinutes: number): boolean {
  if (toMinutes <= fromMinutes) {
    return currentMinutes >= fromMinutes || currentMinutes < toMinutes;
  }

  return currentMinutes >= fromMinutes && currentMinutes < toMinutes;
}

function isWithinPreviousDayOvernightSlot(
  openSlots: readonly OpeningHour[],
  dayOfWeek: number,
  currentMinutes: number
): boolean {
  const previousDayOfWeek = dayOfWeek === 1 ? 7 : dayOfWeek - 1;

  return openSlots.some((openingHour) => {
    if (openingHour.dayOfWeek !== previousDayOfWeek) {
      return false;
    }

    const fromMinutes = timeToMinutes(openingHour.from);
    const toMinutes = timeToMinutes(openingHour.to);

    return toMinutes <= fromMinutes && currentMinutes < toMinutes;
  });
}

function mapDaySlots(openSlots: readonly OpeningHour[], dayOfWeek: number): DayTimeSlot[] {
  return openSlots
    .filter((openingHour) => openingHour.dayOfWeek === dayOfWeek)
    .map((openingHour) => ({
      fromMinutes: timeToMinutes(openingHour.from),
      toMinutes: timeToMinutes(openingHour.to),
      fromFormatted: toTimeInputValue(openingHour.from),
    }))
    .sort((leftSlot, rightSlot) => leftSlot.fromMinutes - rightSlot.fromMinutes);
}

function findNextOpenDay(
  openSlots: readonly OpeningHour[],
  currentDayOfWeek: number
): NextOpenDay | null {
  for (let dayOffset = 1; dayOffset <= 7; dayOffset += 1) {
    const dayOfWeek = ((currentDayOfWeek - 1 + dayOffset) % 7) + 1;
    const daySlots = mapDaySlots(openSlots, dayOfWeek);
    const firstSlot = daySlots[0];

    if (firstSlot) {
      return {
        dayOfWeek,
        daysUntilNextOpen: dayOffset,
        openingTime: firstSlot.fromFormatted,
      };
    }
  }

  return null;
}

function createClosedTodayStatus(nextOpenDay: NextOpenDay | null): OpeningHourStatus {
  return {
    isWithinOpeningHours: false,
    nextOpeningTime: nextOpenDay?.openingTime ?? null,
    isClosedToday: true,
    nextOpenDayOfWeek: nextOpenDay?.dayOfWeek ?? null,
    daysUntilNextOpen: nextOpenDay?.daysUntilNextOpen ?? null,
  };
}

function createOutsideHoursStatus(
  nextOpeningTime: string | null,
  nextOpenDay: NextOpenDay | null = null
): OpeningHourStatus {
  return {
    isWithinOpeningHours: false,
    nextOpeningTime: nextOpenDay?.openingTime ?? nextOpeningTime,
    isClosedToday: false,
    nextOpenDayOfWeek: nextOpenDay?.dayOfWeek ?? null,
    daysUntilNextOpen: nextOpenDay?.daysUntilNextOpen ?? null,
  };
}

export function getOpeningHourStatus(
  openingHours: readonly OpeningHour[],
  now: Date = new Date()
): OpeningHourStatus {
  if (openingHours.length === 0) {
    return OPEN_STATUS;
  }

  const timezoneOffsetMinutes = resolveTimezoneOffsetMinutes(openingHours);
  const { dayOfWeek: currentDayOfWeek, minutesOfDay: currentMinutes } = getRestaurantLocalParts(
    now,
    timezoneOffsetMinutes
  );

  const openSlots = openingHours.filter((openingHour) => openingHour.isOpen);

  if (openSlots.length === 0) {
    return createClosedTodayStatus(null);
  }

  const todaySlots = mapDaySlots(openSlots, currentDayOfWeek);

  const isOpenNow =
    isWithinPreviousDayOvernightSlot(openSlots, currentDayOfWeek, currentMinutes) ||
    todaySlots.some((slot) => isWithinSlot(currentMinutes, slot.fromMinutes, slot.toMinutes));

  if (isOpenNow) {
    return OPEN_STATUS;
  }

  if (todaySlots.length === 0) {
    return createClosedTodayStatus(findNextOpenDay(openSlots, currentDayOfWeek));
  }

  const nextSameDaySlot = todaySlots.find((slot) => slot.fromMinutes > currentMinutes);
  if (nextSameDaySlot) {
    return createOutsideHoursStatus(nextSameDaySlot.fromFormatted);
  }

  return createOutsideHoursStatus(null, findNextOpenDay(openSlots, currentDayOfWeek));
}
